import { X } from 'lucide-react';
import { useEffect, useId, useRef, type ReactNode } from 'react';

import { cn } from '../../lib/cn.js';

/** O primeiro controle do corpo, fora abas e trilha — o porquê está no efeito de abrir. */
const FOCO_PADRAO =
  '[data-modal-body] :is(input, textarea, select, button):not([role="tab"], [data-trilha] *)';

/**
 * Usa `<dialog>` nativo: o navegador entrega prisão de foco, `Esc` para fechar
 * e retorno do foco ao gatilho — comportamentos exigidos por design.md §13
 * que reimplementados em JavaScript sempre saem incompletos.
 *
 * A entrada é animada por CSS (`dialog[open]` em `theme.css`): sobe um pouco
 * e cresce de 98%, com a tampa escurecendo no mesmo tempo. Cabeçalho e corpo
 * se separam por espaço; o rodapé leva filete e a superfície um degrau acima,
 * para os botões não se misturarem com o fim de um formulário rolável.
 *
 * Quadrado, como o cartão de obra, a tabela e o campo (design.md §8, Modal): o
 * raio saiu do modal, do rodapé e do botão de fechar.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  flush = false,
  focoInicial,
  onFechadoPeloNavegador,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string | undefined;
  children: ReactNode;
  footer?: ReactNode | undefined;
  /** `2xl` é o da tarefa: abas à esquerda e o conteúdo em duas metades. */
  size?: 'md' | 'lg' | 'xl' | '2xl' | undefined;
  /**
   * Tira o respiro lateral do corpo.
   *
   * Serve ao formulário em etapas, cuja trilha de abas ocupa a lateral inteira
   * do modal: com o padding do corpo, a trilha ficaria flutuando a 24px da
   * borda, como um painel solto em vez da margem da caixa.
   */
  flush?: boolean | undefined;
  /**
   * Onde o foco cai ao abrir, quando não é o primeiro campo do corpo: na
   * confirmação de saída, o botão de ficar — o que não perde nada. Uma lista é
   * ordem de preferência: vale o primeiro seletor que achar alguma coisa. A
   * lista deve ser constante, fora da renderização.
   */
  focoInicial?: string | readonly string[] | undefined;
  /**
   * Quando o navegador fecha o modal sem dar escolha. Sem isto, vale `onClose`.
   *
   * O `Esc` (e o "voltar" do celular) só pode ser segurado se houve um clique
   * ou uma tecla desde a última vez: é a proteção do navegador contra página
   * que prende a pessoa. Repetido sem nada no meio, ele fecha a caixa por conta
   * própria — e o modal que pergunta antes de sair não tem como perguntar. Este
   * aviso é a chance de sair do jeito certo, guardando o que foi escrito.
   */
  onFechadoPeloNavegador?: (() => void) | undefined;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  /*
   * Id próprio de cada modal: a confirmação de saída abre por cima do
   * assistente, e com um id fixo as duas caixas apontavam para o mesmo título —
   * o leitor de tela anunciava a de baixo.
   */
  const tituloId = useId();
  /** O `open` de agora, para o evento `close` saber se o fechamento veio daqui. */
  const abertoRef = useRef(open);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    abertoRef.current = open;

    if (open && !dialog.open) {
      dialog.showModal();

      /*
       * `showModal` põe o foco no primeiro elemento focável, que aqui é o "X"
       * de fechar — e o anel de foco aparecia em cima dele toda vez que um
       * modal abria, como se sair fosse a ação sugerida.
       *
       * A busca é dentro do corpo, e não do diálogo inteiro, justamente para
       * pular o "X" do cabeçalho. E alcança `button` porque nem todo primeiro
       * controle é campo de texto: no "Incluir na equipe" é um menu suspenso, e
       * com a busca restrita a `input` o foco voltava a cair no fechar.
       *
       * Aba fica de fora. No cadastro de obra o primeiro botão do corpo passou
       * a ser uma aba da trilha lateral, e o foco caía nela — o anel aparecia
       * sobre o sumário, em vez de sobre o nome da obra, que é onde se começa a
       * escrever. Aba é navegação, não campo. Pela mesma razão fica de fora a
       * trilha de um assistente em etapas (`data-trilha`).
       *
       * Marcar o campo com `autoFocus` não resolveria: o React não escreve o
       * atributo no DOM, ele chama `.focus()` na montagem — e este efeito, que
       * roda depois, tomaria o foco de volta.
       */
      const preferencias =
        typeof focoInicial === 'string' ? [focoInicial] : (focoInicial ?? [FOCO_PADRAO]);
      for (const seletor of preferencias) {
        const alvo = dialog.querySelector<HTMLElement>(seletor);
        if (alvo) {
          alvo.focus();
          break;
        }
      }
    }

    if (!open && dialog.open) dialog.close();
  }, [open, focoInicial]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    /*
     * `Esc` dispara `cancel`, que é segurado aqui: quem decide se fecha é
     * `onClose` — a página fecha, ou pergunta antes. Sem isto o React ficaria
     * com `open` dessincronizado.
     *
     * Quando o `cancel` vem sem poder ser segurado (`cancelable` falso), o
     * navegador vai fechar de qualquer jeito; quem trata é o `close`, abaixo.
     */
    const handleCancel = (event: Event) => {
      if (!event.cancelable) return;
      event.preventDefault();
      onClose();
    };

    /*
     * O diálogo fechou. Se o `open` ainda diz aberto — e não reabriu no meio
     * tempo —, quem fechou foi o navegador, e a página precisa saber.
     */
    const handleClose = () => {
      if (!abertoRef.current || dialog.open) return;
      (onFechadoPeloNavegador ?? onClose)();
    };

    dialog.addEventListener('cancel', handleCancel);
    dialog.addEventListener('close', handleClose);
    return () => {
      dialog.removeEventListener('cancel', handleCancel);
      dialog.removeEventListener('close', handleClose);
    };
  }, [onClose, onFechadoPeloNavegador]);

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={tituloId}
      className={cn(
        /*
         * `m-auto` é o que centraliza. O navegador centraliza um <dialog> modal
         * com `inset: 0; margin: auto`, mas o preflight do Tailwind zera a
         * margem de todo elemento — sem isto o modal cola no canto superior
         * esquerdo.
         */
        'm-auto w-[calc(100vw-2rem)] border border-line bg-surface p-0 text-ink shadow-pop',
        'backdrop:bg-scrim',
        // Em tela pequena vira bottom sheet: margem embaixo zerada puxa o modal para baixo.
        'max-sm:mx-0 max-sm:mb-0 max-sm:w-full max-sm:max-w-full',
        size === '2xl'
          ? 'max-w-[1240px]'
          : size === 'xl'
            ? 'max-w-[1040px]'
            : size === 'lg'
              ? 'max-w-[880px]'
              : 'max-w-[560px]',
      )}
      onClick={(event) => {
        // Clique no backdrop atinge o próprio <dialog>, não seu conteúdo.
        if (event.target === dialogRef.current) onClose();
      }}
    >
      <div className="flex max-h-[85dvh] flex-col">
        <header className="flex items-start justify-between gap-4 px-6 pt-6 pb-4">
          <div>
            <h2 id={tituloId} className="text-[17px] font-semibold tracking-tight">
              {title}
            </h2>
            {description ? <p className="mt-1 text-[13px] text-ink-3">{description}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="-mt-1 -mr-2 grid size-8 shrink-0 place-items-center text-ink-3 transition-colors hover:bg-sunken hover:text-ink"
          >
            <X size={18} strokeWidth={1.75} aria-hidden />
          </button>
        </header>

        <div
          data-modal-body
          className={cn('flex-1 overflow-y-auto', flush ? 'px-0 pb-0' : 'px-6 pb-5')}
        >
          {children}
        </div>

        {footer ? (
          // Quebra a linha quando os botões não cabem: três escolhas num celular estreito.
          <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-line bg-surface-2 px-6 py-4">
            {footer}
          </footer>
        ) : null}
      </div>
    </dialog>
  );
}
